import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api, API_URL, resolveMediaUrl } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import NumberInput from '../../components/NumberInput';
import { useNotification } from '../../contexts/NotificationContext';

function ProductsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', category: '', photo_url: null });
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '' });
  const [editingCategory, setEditingCategory] = useState(null);

  const shopId = user?.shop_id;
  const branchId = user?.branch_id;
  const [selectedBranchId, setSelectedBranchId] = useState(branchId || null);
  const [branches, setBranches] = useState([]);

  const targetBranchId = selectedBranchId || branchId;

  const fetchBranches = async () => {
    if (shopId) {
      try {
        const data = await api.getBranches(null, shopId);
        setBranches(data);
        if (data.length > 0 && !selectedBranchId) setSelectedBranchId(data[0].id);
      } catch (err) { console.error(err); }
    }
  };

  const fetchProducts = async () => {
    if (!selectedBranchId && !branchId) return;
    try {
      const data = await api.getManagerProducts();
      const branchFilterId = selectedBranchId || branchId;
      setProducts(branchFilterId ? data.filter(p => p.branch_id === branchFilterId) : data);
    }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchProductCategories = async () => {
    if (!targetBranchId) return;
    try {
      const data = await api.getManagerProductCategories(targetBranchId);
      setProductCategories(data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { if (shopId) fetchBranches(); }, [shopId]);
  useEffect(() => { fetchProducts(); }, [selectedBranchId]);
  useEffect(() => { fetchProductCategories(); }, [targetBranchId]);

  const openCreate = () => {
    if (!targetBranchId) { showAlert(t('admin.pleaseSelectBranch')); return; }
    setEditing('create'); setForm({ name: '', price: '', category: '', photo_url: null }); setPendingPhotoFile(null);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, price: p.price, category: p.category ?? '', photo_url: p.photo_url ?? null });
    setPendingPhotoFile(null);
  };

  const uploadPhoto = async (e) => {
    if (!editing || editing === 'create' || !e.target?.files?.[0]) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) { showAlert(t('manager.pleaseSelectImageFile')); return; }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const updated = await api.uploadManagerProductPhoto(editing, fd);
      setForm(f => ({ ...f, photo_url: updated.photo_url }));
      fetchProducts();
    } catch (err) { showAlert(err.message); }
    e.target.value = '';
  };

  const deletePhoto = async () => {
    if (!editing || editing === 'create' || !form.photo_url) return;
    showConfirm(t('admin.deletePhotoConfirm', 'Delete this photo?'), async () => {
      try {
        const updated = await api.deleteManagerProductPhoto(editing);
        setForm(f => ({ ...f, photo_url: updated.photo_url ?? null }));
        fetchProducts();
      } catch (err) { showAlert(err.message); }
    });
  };

  const save = async () => {
    if (!targetBranchId) { showAlert(t('admin.branchAndNameRequired')); return; }
    try {
      const payload = { branch_id: targetBranchId, name: form.name, price: parseFloat(form.price), category: form.category || null };
      if (editing === 'create') {
        const created = await api.createManagerProduct(payload);
        if (pendingPhotoFile) {
          const fd = new FormData();
          fd.append('file', pendingPhotoFile);
          await api.uploadManagerProductPhoto(created.id, fd);
        }
        setEditing(null);
        setPendingPhotoFile(null);
        fetchProducts();
      } else {
        await api.updateManagerProduct(editing, { name: form.name, price: parseFloat(form.price), category: form.category || null });
        setEditing(null);
        fetchProducts();
      }
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteProductConfirm'), async () => {
      try { await api.deleteManagerProduct(id); fetchProducts(); }
      catch (err) { showAlert(err.message); }
    });
  };

  const openAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '' });
    setShowCategoryModal(true);
  };
  const openEditCategory = (c) => {
    setEditingCategory(c.id);
    setCategoryForm({ name: c.name });
    setShowCategoryModal(true);
  };
  const saveCategory = async () => {
    if (!targetBranchId || !categoryForm.name.trim()) return;
    try {
      if (editingCategory) {
        await api.updateManagerProductCategory(editingCategory, { name: categoryForm.name.trim() });
      } else {
        await api.createManagerProductCategory({ branch_id: targetBranchId, name: categoryForm.name.trim() });
      }
      setShowCategoryModal(false);
      fetchProductCategories();
    } catch (err) { showAlert(err.message); }
  };
  const removeCategory = async (id) => {
    showConfirm(t('admin.deleteProductCategoryConfirm'), async () => {
      try { await api.deleteManagerProductCategory(id); fetchProductCategories(); setShowCategoryModal(false); }
      catch (err) { showAlert(err.message); }
    });
  };

  return (
    <AdminLayout role="manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.products')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        {targetBranchId && (
          <button style={S.btnOrange} onClick={openCreate}
            onMouseEnter={e => e.target.style.background = T.orangeHov}
            onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addProduct')}</button>
        )}
      </div>

      {shopId && branches.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.selectBranch')}</label>
          <select value={selectedBranchId || ''} onChange={e => setSelectedBranchId(parseInt(e.target.value) || null)} style={{ ...S.select, width: 'auto', marginBottom: 0 }}>
            <option value="">-- Select Branch --</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}

      {targetBranchId && (
        <div style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: T.white }}>{t('admin.productCategories')}</h3>
            <button onClick={openAddCategory} style={S.btnSmall}>{t('admin.addProductCategory')}</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {productCategories.length === 0 && <span style={{ color: T.muted, fontSize: '0.85rem' }}>{t('admin.noProductCategories')}</span>}
            {productCategories.map(c => (
              <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.6rem', background: 'rgba(255,255,255,0.06)', borderRadius: 4, fontSize: '0.85rem' }}>
                {c.name}
                <button type="button" onClick={() => openEditCategory(c)} style={{ background: 'none', border: 'none', color: T.orange, cursor: 'pointer', fontSize: '0.75rem' }}>{t('common.edit')}</button>
                <button type="button" onClick={() => removeCategory(c.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>{t('common.delete')}</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {showCategoryModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editingCategory ? t('admin.editProductCategory') : t('admin.addProductCategory')}</h2>
            <input placeholder={t('manager.placeholderCategory')} value={categoryForm.name} onChange={e => setCategoryForm({ name: e.target.value })} style={S.input} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={saveCategory} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setShowCategoryModal(false)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newProduct') : t('admin.editProduct')}</h2>
            <input placeholder={t('manager.placeholderName')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />
            <NumberInput placeholder={t('manager.placeholderPrice')} value={form.price} onChange={(v) => setForm({ ...form, price: v })} inputStyle={S.input} min={0} step={0.01} />
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.category')}</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={S.input}>
                <option value="">-- {t('admin.selectCategory')} --</option>
                {productCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <button type="button" onClick={openAddCategory} style={{ ...S.btnSmall, marginTop: '0.4rem' }}>+ {t('admin.addNewCategory')}</button>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.photo')}</label>
              {editing === 'create' ? (
                <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                  {pendingPhotoFile ? t('manager.photoSelected', { name: pendingPhotoFile.name }) : t('manager.uploadPhoto')}
                  <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && setPendingPhotoFile(e.target.files[0])} style={{ display: 'none' }} />
                </label>
              ) : form.photo_url ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <img src={resolveMediaUrl(form.photo_url)} alt="" loading="lazy" style={{ width: 64, height: 64, objectFit: 'contain', background: 'var(--bg-dark)', borderRadius: 8, border: `1px solid ${T.border || 'var(--border)'}` }} />
                  <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                    {t('manager.changePhoto')}
                    <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                  </label>
                  <button type="button" onClick={deletePhoto} style={{ ...S.btnDanger, padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}>
                    {t('common.delete')}
                  </button>
                </div>
              ) : (
                <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                  {t('manager.uploadPhoto')}
                  <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                </label>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{[t('admin.id'), t('admin.name'), t('admin.price'), t('admin.category'), t('admin.actions')].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{p.id}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {p.photo_url && <img src={resolveMediaUrl(p.photo_url)} alt="" style={{ width: 32, height: 32, objectFit: 'contain', background: 'var(--bg-dark)', borderRadius: 4 }} />}
                      {p.name}
                    </div>
                  </td>
                  <td style={{ ...S.td, color: T.orange }}>{p.price} EGP</td>
                  <td style={S.td}>{p.category ?? '—'}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={S.btnSmall} onClick={() => openEdit(p)}
                        onMouseEnter={e => e.target.style.color = T.white}
                        onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                      <button style={S.btnDanger} onClick={() => remove(p.id)}>{t('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Products() {
  return <ProtectedRoute requireSalonManager><ProductsContent /></ProtectedRoute>;
}
